/**
 * Le petit rond du tutoriel.
 *
 * Ce fichier ne porte que le RENDU. Ce qu'on montre du doigt à quel temps vit
 * dans `scenario.ts` (`COUPS_DE_POUCE`), avec les autres tables indexées sur
 * `Etape`. La ligne qui dit OÙ cliquer vit dans le bandeau (`TaMission`)
 * depuis le 06-10-2026 : au-dessus du composeur, elle disparaissait dès que le
 * candidat n'avait aucune conversation ouverte — le moment où il en a le plus
 * besoin.
 */
import type { ReactNode } from "react";

import { cn } from "@/core/utils";

/**
 * Le rond qui pulse autour d'un bouton.
 *
 * Deux anneaux superposés : l'un fixe (on voit toujours QUOI est visé, même à
 * mi-battement), l'autre en `animate-ping` (c'est lui qu'on voit du coin de
 * l'œil). `motion-reduce:animate-none` coupe le battement pour qui a demandé
 * moins d'animations — l'anneau fixe suffit alors.
 *
 * 🪤 Un CONTOUR, jamais un fond teinté : `border-X bg-X/10` est interdit dans
 * ce dépôt (cf. `cockpit/CLAUDE.md`), et un fond par-dessus un bouton mangerait
 * l'icône qu'on essaie justement de montrer. `pointer-events-none` sur les deux
 * anneaux — le rond ne doit jamais voler le clic qu'il réclame.
 *
 * 🪤 `regie-round`, PAS `rounded-full` ; `sf-arrondi`, PAS `rounded-xl` (même raison). Le cockpit force `border-radius: 8px`
 * sur `*` en `!important` (`globals.css`, layer base) : `rounded-full` y rend un
 * carré à coins arrondis, mesuré à 8 px sur cet écran. La seule sortie prévue
 * est cette classe-là, du même layer et de spécificité supérieure — et Kayla a
 * demandé « un petit rond, comme dans un jeu », pas un cadre. Le champ de
 * saisie, lui, garde l'arrondi de l'app : c'est un rectangle large.
 */
export function Halo({
  actif,
  forme = "rond",
  className,
  children,
}: {
  actif: boolean;
  /** `rond` pour un bouton de 36 px ou une pilule (un onglet), `champ` pour la zone de saisie ou une carte. */
  forme?: "rond" | "champ";
  className?: string;
  children: ReactNode;
}) {
  // `sf-arrondi` (16 px) est la classe de la librairie qui passe devant l'arrondi global : un
  // anneau de 6 px autour d'un champ à 12 px ou d'une carte à 16 px.
  const rayon = forme === "rond" ? "regie-round" : "sf-arrondi";
  return (
    <span className={cn("relative inline-flex", className)}>
      {actif && (
        <>
          <span
            aria-hidden
            className={cn(
              "pointer-events-none absolute -inset-1.5 border-2 border-mark",
              "animate-ping motion-reduce:animate-none",
              rayon,
            )}
          />
          <span
            aria-hidden
            className={cn(
              "pointer-events-none absolute -inset-1.5 border-2 border-mark",
              rayon,
            )}
          />
        </>
      )}
      {children}
    </span>
  );
}
